import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// API service functions
export const projectsAPI = {
  // Get projects with filters
  getProjects: async (filters = {}) => {
    const params = new URLSearchParams();
    
    // Convert frontend parameter names to backend parameter names
    const paramMapping = {
      'financialYear': 'financial_year',
      'fundCategory': 'fund_category',
      'fundHead': 'fund_head',
      'projectStage': 'project_stage'
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        const backendKey = paramMapping[key] || key;
        params.append(backendKey, value);
      }
    });
    
    const response = await apiClient.get(`/projects?${params.toString()}`);
    return response.data;
  },

  // Create new project
  createProject: async (projectData) => {
    const response = await apiClient.post('/projects', projectData);
    return response.data;
  },
};

export const departmentsAPI = {
  // Get department statistics  
  getStats: async (filters = {}) => {
    const params = new URLSearchParams();
    
    // Convert frontend parameter names to backend parameter names
    const paramMapping = {
      'financialYear': 'financial_year',
      'fundCategory': 'fund_category',
      'fundHead': 'fund_head',
      'projectStage': 'project_stage'
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        const backendKey = paramMapping[key] || key;
        params.append(backendKey, value);
      }
    });

    const response = await apiClient.get(`/departments/stats?${params.toString()}`);
    return response.data;
  },
};

export const filtersAPI = {
  // Get filter options
  getOptions: async () => {
    const response = await apiClient.get('/filters');
    return response.data;
  },
};

export const fundHeadsAPI = {
  // Get fund heads (optionally filtered by category)
  getFundHeads: async (fundCategory = null) => {
    const params = new URLSearchParams();
    if (fundCategory && fundCategory !== 'all') {
      params.append('fund_category', fundCategory);
    }
    const response = await apiClient.get(`/fund-heads?${params.toString()}`);
    return response.data;
  },

  // Get all fund categories
  getFundCategories: async () => {
    const response = await apiClient.get('/fund-categories');
    return response.data;
  },
};

export const aiAPI = {
  // Get AI insights
  getInsights: async (filters = {}) => {
    const params = new URLSearchParams();
    
    // Convert frontend parameter names to backend parameter names
    const paramMapping = {
      'financialYear': 'financial_year',
      'fundCategory': 'fund_category',
      'fundHead': 'fund_head',
      'projectStage': 'project_stage'
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        const backendKey = paramMapping[key] || key;
        params.append(backendKey, value);
      }
    });

    const response = await apiClient.get(`/ai/insights?${params.toString()}`);
    return response.data;
  },

  // Send chat message
  sendMessage: async (message, context = {}) => {
    const response = await apiClient.post('/ai/chat', {
      message,
      context
    });
    return response.data;
  },
};

export const liveDataAPI = {
  // Get live projects with filters
  getProjects: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });
    
    const response = await apiClient.get(`/live/projects?${params.toString()}`);
    return response.data;
  },

  // Get live department statistics  
  getDepartmentStats: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });

    const response = await apiClient.get(`/live/departments/stats?${params.toString()}`);
    return response.data;
  },

  // Get live fund categories
  getFundCategories: async () => {
    const response = await apiClient.get('/live/fund-categories');
    return response.data;
  },

  // Get live fund heads (with category filtering)
  getFundHeads: async (category = null) => {
    const params = new URLSearchParams();
    if (category && category !== 'all') {
      params.append('category', category);
    }
    const response = await apiClient.get(`/live/fund-heads?${params.toString()}`);
    return response.data;
  },

  // Get live divisions/departments
  getDivisions: async () => {
    const response = await apiClient.get('/live/divisions');
    return response.data;
  },

  // Get live financial years
  getFinancialYears: async () => {
    const response = await apiClient.get('/live/financial-years');
    return response.data;
  },

  // Get all live filter options
  getFilterOptions: async () => {
    const response = await apiClient.get('/live/filters');
    return response.data;
  },
};

export const iwmsAPI = {
  // Get project summary with stage breakdown using exact business logic
  getProjectSummary: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });
    
    const response = await apiClient.get(`/iwms/project-summary?${params.toString()}`);
    return response.data;
  },

  // Get projects using exact business logic (TS-based)
  getProjects: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });
    
    const response = await apiClient.get(`/iwms/projects?${params.toString()}`);
    return response.data;
  },

  // Get department statistics using exact business logic
  getDepartmentStats: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });

    const response = await apiClient.get(`/iwms/departments/stats?${params.toString()}`);
    return response.data;
  },

  // Get fund categories with project counts (from head_master via TS relationship)
  getFundCategories: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });
    
    const response = await apiClient.get(`/iwms/fund-categories?${params.toString()}`);
    return response.data;
  },

  // Get fund heads with project counts (cascading by category_id)
  getFundHeads: async (category_id = null, filters = {}) => {
    const params = new URLSearchParams();
    if (category_id && category_id !== 'all') {
      params.append('category_id', category_id);
    }
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });
    
    const response = await apiClient.get(`/iwms/fund-heads?${params.toString()}`);
    return response.data;
  },

  // Get departments/divisions  
  getDepartments: async () => {
    const response = await apiClient.get('/iwms/departments');
    return response.data;
  },

  // Get financial years
  getFinancialYears: async () => {
    const response = await apiClient.get('/iwms/financial-years');
    return response.data;
  },

  // Get project stages
  getProjectStages: async () => {
    const response = await apiClient.get('/iwms/project-stages');
    return response.data;
  },

  // Get all filter options
  getFilterOptions: async () => {
    const response = await apiClient.get('/iwms/filters');
    return response.data;
  },

  // Get project documents
  getProjectDocuments: async (projectId) => {
    const response = await apiClient.get(`/iwms/projects/${projectId}/documents`);
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await apiClient.get('/');
    return response.data;
  } catch (error) {
    throw new Error('Backend connection failed');
  }
};

// Connection status check endpoint
const getConnectionStatus = async () => {
  const response = await apiClient.get('/connection/status');
  return response.data;
};

export default {
  projects: projectsAPI,
  departments: departmentsAPI,
  filters: filtersAPI,
  fundHeads: fundHeadsAPI,
  ai: aiAPI,
  liveData: liveDataAPI,
  iwms: iwmsAPI,  // Your exact business logic endpoints
  healthCheck,
  getConnectionStatus,
};